import { useEffect } from 'react';

import { useLocation } from 'react-router-dom';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';

import * as ROUTES from '#client/constants/routes';

/**
 * Custom hook to handle authentication checks and redirects
 *
 * Determines if authentication is required based on user mode and handles redirects.
 * Anonymous and standalone modes don't require authentication, unless userUuid is
 * present — which indicates an expired session rather than a truly anonymous user.
 * Starter and business modes require authentication.
 *
 * @param {object} userMode - User mode object with mode and flags
 * @param {boolean} authenticated - Whether user is authenticated
 * @param {string} threadUuid - Current thread UUID
 * @param {string} redirectTo - Optional redirect URL after login (defaults to current page)
 * @param {Function} onAuthRequired - Callback when auth is required
 * @param {Function} onAuthValid - Callback when auth is valid
 */
export function useThreadAuthentication(
  userMode,
  authenticated,
  threadUuid,
  redirectTo,
  onAuthRequired,
  onAuthValid
) {
  const { login, userUuid } = useAuthentication();
  const location = useLocation();

  useEffect(() => {
    if (!userMode) return;

    if (userMode.isAnonymous || userMode.isStandalone) {
      // userUuid present despite anonymous mode means the session expired —
      // useUserMode falls back to anonymous when authenticated=false, but the
      // stale token still carries the uuid. Redirect to login so the user can
      // re-authenticate and return to the page they were on.
      if (userUuid) {
        onAuthRequired();
        login(redirectTo || location.pathname);
        return;
      }
      // Truly anonymous user — no auth needed
      onAuthValid();
      return;
    }

    // Starter and business modes require authentication.
    // Use chatEntry (not location.pathname) as fallback to prevent returning a user to an
    // anonymous-thread URL after login — that would produce a 403 because the thread
    // belongs to a different identity (visitor vs customer).
    if (!authenticated) {
      onAuthRequired();
      login(redirectTo || ROUTES.chatEntry);
      return;
    }

    onAuthValid();
  }, [
    userMode,
    authenticated,
    userUuid,
    login,
    redirectTo,
    location.pathname,
    // threadUuid is not read inside the effect but is included so that navigating to a
    // different thread re-triggers the auth check (the new thread may have different
    // auth requirements, e.g. switching from an anonymous thread to a business thread).
    threadUuid,
    onAuthRequired,
    onAuthValid
  ]);
}
